import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, IconBug, IconExternalLink } from '@screentone/core';

import { useIsAdmin, useIsDebug } from 'hooks/roles';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import env from 'utils/env';

export enum ExternalLinkContentType {
  COLLECTION = 'collection',
  ARTICLE = 'article',
  NEWSLETTER = 'newsletter'
}

interface ExternalLinkProps {
  contentId: string;
  isPublished: boolean;
  type: ExternalLinkContentType;
  embargo?: boolean;
  margin?: Record<string, string>;
  additionalContentId?: string;
  debug?: boolean;
}

const ExternalLink = ({
  contentId,
  isPublished,
  type,
  embargo = false,
  margin = {},
  additionalContentId,
  debug = true
}: ExternalLinkProps) => {
  const isAdmin = useIsAdmin();
  const isDebug = useIsDebug();
  const isCreatingNewPage = useIsNewSectionPage();
  const { currentProperty: routeProperty } = useAuth(); // Original property from URL for navigation

  // Show external link for admins or debug users only (debug users can view admin tools)
  if ((!isAdmin && !isDebug) || !isPublished || contentId === '') return null;
  const EMBARGO_PATH = 'preview/content';
  const LIVE_PATH = `${type === ExternalLinkContentType.ARTICLE ? 'history' : 'collections'}/byoriginid`;
  const PATH = embargo ? EMBARGO_PATH : LIVE_PATH;

  const handleClickAllesseh = () => {
    const allessehUrl = `${env.ALLESSEH_ADMIN_TOOL}${PATH}/${contentId}`;
    window.open(allessehUrl, '_blank');

    if (additionalContentId) {
      const url = `${env.ALLESSEH_ADMIN_TOOL}${PATH}/${additionalContentId}`;
      window.open(url, '_blank');
    }
  };

  const handleClickSummaries = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const allessehUrl = `${env.ALLESSEH_API_COLLECTION_SUMMARIES_URL}/${contentId}`;
    window.open(allessehUrl, '_blank');

    if (additionalContentId) {
      const url = `${env.ALLESSEH_API_COLLECTION_SUMMARIES_URL}/${additionalContentId}`;
      window.open(url, '_blank');
    }
  };

  const handleClickDebug = () => {
    const debugUrl = `/${routeProperty}/debug/${contentId}`;
    window.open(debugUrl, '_blank');
  };

  return (
    <>
      <Button
        margin={margin}
        data-testid="external-link-open-allesseh-button"
        tertiary
        icon={IconExternalLink}
        iconPosition="right"
        onClick={handleClickAllesseh}
        disabled={isCreatingNewPage}
      >
        {type !== ExternalLinkContentType.ARTICLE ? 'Allesseh Admin' : 'Open in Allesseh Admin Tool'}
      </Button>
      {type !== ExternalLinkContentType.ARTICLE && (
        <Button
          data-testid="external-link-open-collectionsummary-button"
          tertiary
          componentEl="a"
          href={`${env.ALLESSEH_API_COLLECTION_SUMMARIES_URL}/${contentId}`}
          target="_blank"
          rel="noopener noreferrer"
          icon={IconExternalLink}
          iconPosition="right"
          onClick={handleClickSummaries}
          disabled={isCreatingNewPage}
        >
          Summaries
        </Button>
      )}
      {debug && (
        <Button
          data-testid="external-link-debug-page-button"
          tertiary
          onClick={handleClickDebug}
          icon={IconBug}
          disabled={isCreatingNewPage}
        >
          {type !== ExternalLinkContentType.ARTICLE && 'Debug'}
        </Button>
      )}
    </>
  );
};

export default ExternalLink;
